import React from 'react';

/**
 * Button Component - Atomic Level
 * 
 * A flexible, accessible button component with multiple variants,
 * sizes, and states. Follows design system principles and best practices.
 * 
 * Features:
 * - Multiple variants (primary, secondary, outline, ghost)
 * - Size variations
 * - Loading state with spinner
 * - Disabled state
 * - Full width option
 * - Icon support
 * - Accessible (ARIA attributes, keyboard navigation)
 */

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const variantClasses: Record<ButtonVariant, string> = {
  primary: 
    'bg-[#EAB308] text-[#0B0B0F] shadow-md hover:shadow-lg hover:bg-[#CA8A04] focus:ring-[#EAB308]/30 transition-all font-semibold',
  secondary: 
    'bg-[#52525B] text-white shadow-md hover:bg-[#3F3F46] hover:shadow-lg focus:ring-[#52525B]/30',
  accent: 
    'bg-[#FACC15] text-[#0B0B0F] shadow-md hover:bg-[#EAB308] hover:shadow-lg focus:ring-[#EAB308]/30 font-semibold',
  outline: 
    'border-2 border-[#EAB308] text-[#EAB308] bg-transparent hover:bg-[#EAB308] hover:text-[#0B0B0F] focus:ring-[#EAB308]/30',
  ghost: 
    'text-[#52525B] hover:bg-[#F4F4F5] focus:ring-[#E4E4E7]',
  danger:
    'bg-red-600 text-white shadow-md hover:bg-red-700 hover:shadow-lg focus:ring-red-500/30',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2.5 text-base',
  lg: 'px-6 py-3 text-lg',
  xl: 'px-8 py-4 text-xl',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Loading state - shows spinner and disables button */
  isLoading?: boolean;
  /** Icon to display before the button text */
  leftIcon?: React.ReactNode;
  /** Icon to display after the button text */
  rightIcon?: React.ReactNode;
  /** Make button span full width of container */
  fullWidth?: boolean;
  /** ARIA label for accessibility */
  ariaLabel?: string;
}

/**
 * Loading Spinner Component
 */
const Spinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`animate-spin h-5 w-5 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      ariaLabel,
      disabled,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';
    const widthClass = fullWidth ? 'w-full' : 'w-auto';
    
    const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`;
    
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        aria-label={ariaLabel}
        aria-busy={isLoading}
        className={buttonClasses}
        {...props}
      >
        {isLoading && <Spinner />}
        {!isLoading && leftIcon && <span className="inline-flex">{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span className="inline-flex">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

